import Head from "next/head";
import { MenuId } from "../components/Header";
import Layout from "../components/Layout";
import styles from "./model-application-form.module.scss"
import { Button, Checkbox, FocusTrap, Group, List, Paper, Progress, rem, Select, Space, Text, Textarea, TextInput } from "@mantine/core";
import Link from "next/link";
import { useForm } from '@mantine/form'
import PhoneInput from "react-phone-input-2";
import { useState } from "react";
import 'react-phone-input-2/lib/style.css'

import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage } from "@fortawesome/sharp-solid-svg-icons";
import axios, { AxiosProgressEvent } from "axios";
import { okModal } from "../lib/utils";
import { closeAllModals, openModal } from "@mantine/modals";
import { consoleError } from "../lib/logger";

export default function ApplicationForm()
{
    const [uploading, setUploading] = useState(false);

    const [uploadProgress, setUploadProgress] = useState(0);
    const [showUploadProgress, setShowUploadProgress] = useState(false);

    const [files, setFiles] = useState<File[]>([]);
    const[fileError, setFileError] = useState(false);

    const form = useForm({
        initialValues: {firstName:'', lastName: '', email:'',  privacy: false, files:'', information:'' },
         validate: {
          email: (value) => (/^\S+@\S+$/.test(value) ? null : 'Invalid email'),
          files: (value)=>
          {
            if (files.length==0 || files.length>5)
            {
              setFileError(true);
              return "Please use at most 5 files";
            }
            setFileError(false);

            return null;
          }




        },
      });

      async function updateUploadProgress (e: AxiosProgressEvent)
      {
          setUploadProgress((e.progress??0)*100);
      }  


      async function submit(values: typeof form.values)
      {
        if (files.length==0)
        {
          return;
        }

        let formData = new FormData();

        Object.keys(form.values).forEach(x=>
          {
            formData.append(x, Object(form.values)[x])
          }
          );
        for (let i =0;i<files.length; i++)
        {

            formData.append("file_" + i, files[i]);
        }

        setUploadProgress(0);
        setShowUploadProgress(true);
        setUploading(true);

        try
        {
        await axios.post("/n/api/showcaseapplication",
            formData,
            {
                headers:{'Content-Type': 'multipart/form-data'},
                onUploadProgress: updateUploadProgress,
                withCredentials: true
                                  
            });

            openModal({
              title: 'Thank you!',
              children: <>
              <Text size="sm">Thank you for submitting your showcase application! We&apos;ll get in touch if we decide to feature it on our website.</Text>
              <Button fullWidth onClick={(e)=> {closeAllModals();}} mt="md">
                        Ok
                      </Button>
              </>
      
              
              });

            setShowUploadProgress(false);
        }
        catch(e)
        {
            consoleError(e);
            okModal("There was a problem saving your data. Please email us instead.");
        }
        finally
        {
            setShowUploadProgress(false);
            setUploading(false);
        }

      }


    return(
        <Layout menuId={MenuId.home}>
        <Head>
            <title>Showcase application form</title>
            <meta name='description' content="Application form if you'd like to feature your work Raw Umber Studios's showcase"/>
        </Head>
            
            <h1>Showcase application form</h1>
            <Text mb='sm'>
              We&apos;re launching a section on our website showcasing works that artists have created from our online sessions, inspired by our reference images, or during our workshops.
            </Text>
            <Text mb='sm'>
              If you&apos;d like to be considered then please submit up to 5 images of your work. They can be a single piece, or you can submit more than. If we select your work then we&apos;ll get in touch for more information, and then feature it on our website. Obviously we&apos;ll credit your work, link to your website / Instagram, and do our best to promote it!              
            </Text>

<Space h='lg'/>
<Paper>
<FocusTrap active={true}>
          <form onSubmit={form.onSubmit( (values)=> submit(values))}>                            
              <TextInput label="First name" style={{maxWidth:'400px'}} type="text" required {...form.getInputProps('firstName')}/>
              <TextInput label="Last name" style={{maxWidth:'400px'}} type="text" required {...form.getInputProps('lastName')}/>
              <TextInput label="Email address" style={{maxWidth:'400px'}} type="email" required {...form.getInputProps('email')}/>

<Textarea placeholder="For example, which online session or model they are inspired by; or which workshop they are from"  label='Let us know a little about the works you are submitting' required {...form.getInputProps('information')} />

<Text size='sm' weight={500}>By completing this application form, you agree to the following:</Text>
<Checkbox required mt='xs' label={<>I have read and agreed with the <Link href='/privacy-policy'>privacy policy</Link>, and am happy for Raw Umber to use the image on their website</>} {...form.getInputProps('privacy')}/>

<Space h='lg'/>
<Text size='sm' weight={500}>Please upload a handful of photographs of the works you would like to submit:</Text>
<div style={{position:'relative'}}>
{uploading && <div className={styles.overlay}><div className={styles.loading}> <Text>Uploading ...</Text><Space h='md'/><Progress value={uploadProgress}/></div></div>}



  <Dropzone
      onDrop={(fs) => 
      {
        let fa2 = [... files];
        fs.forEach(x=> fa2.push(x));
        setFiles(fa2);
      }
      }
      maxSize={5 * 1024 ** 2}
      accept={IMAGE_MIME_TYPE}
      maxFiles = {5}
      multiple     
    >
 <Group position="center" spacing="xl"  style={{ minHeight: rem(220), pointerEvents: 'none', position:'relative' }}>



        <Dropzone.Accept>
        <FontAwesomeIcon icon={faImage} size="2xl" />
        </Dropzone.Accept>
        <Dropzone.Reject>
        <FontAwesomeIcon icon={faImage} size="2xl" />
        </Dropzone.Reject>
        <Dropzone.Idle>
        <FontAwesomeIcon icon={faImage} size="2xl" />
        </Dropzone.Idle>

        <div>
          <Text size="xl" inline>
            Drag images here or click to select files
          </Text>
          <Text size="sm" color="dimmed" inline mt={7}>
            Attach up to 5 photos. Each file should not exceed 5mb
          </Text>
        </div>
      </Group>
    </Dropzone>

    {files.length!=0 && <List icon={<FontAwesomeIcon icon={faImage}/>}>
      {files.map(x=> <List.Item key={x.name}>{x.name}</List.Item>)}
      </List>}
    </div>
    {fileError && <Text color='red' size='xs' >Please upload between 1 and 5 files</Text>}

              <Button disabled={uploading} type='submit' mt='sm'>Submit</Button>
                             
            </form>            
            </FocusTrap>
            </Paper>
<Space h='xl'/>
           
            </Layout>
    );
}